import { createAsyncThunk } from "@reduxjs/toolkit";
import config from "../../config";
import axiosClient from "../../common/api/request";

export const getAIChats = createAsyncThunk(
  "aiChats/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.get(
        `${config.API_URL}/ai/chats/`,
        { params },
        config
      );
      return { ...data, page: params.page || 1 };
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const getAIChat = createAsyncThunk(
  "aiChats/getOne",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.get(
        `${config.API_URL}/ai/chats/${params.id}/`,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const createAIChat = createAsyncThunk(
  "aiChats/create",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.post(
        `${config.API_URL}/ai/chats/`,
        params,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const renameAIChat = createAsyncThunk(
  "aiChats/rename",
  async (params, { rejectWithValue }) => {
    const { id, ...networkParams } = params;
    try {
      const data = await axiosClient.patch(
        `${config.API_URL}/ai/chats/${params.id}/`,
        networkParams,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const deleteAIChat = createAsyncThunk(
  "aiChats/delete",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.delete(
        `${config.API_URL}/ai/chats/${params.id}/`,
        config
      );
      return {
        data,
        id: params.id,
      };
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const clearAIChatMessages = createAsyncThunk(
  "aiChats/clearMessages",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.delete(
        `${config.API_URL}/ai/chats/${params.id}/messages/`,
        config
      );
      return {
        data,
        id: params.id,
      };
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const getAICommands = createAsyncThunk(
  "aiChats/getCommands",
  async (_, { rejectWithValue }) => {
    try {
      const data = await axiosClient.get(
        `${config.API_URL}/ai/commands/`,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const sendAIQuery = createAsyncThunk(
  "aiChats/sendQuery",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.post(
        `${config.API_URL}/ai/`,
        params,
        { timeout: 180000 }
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error:
            error.code === "ECONNABORTED"
              ? "The request took too long. Please try again."
              : error.message,
        });
      }
    }
  }
);

// The help assistant answers "how do I ...?" from the product guide. It shares the
// chat list/create/rename/delete endpoints above (threads carry kind: "help") but has
// its own question endpoint and its own daily quota.
export const sendAIHelpQuery = createAsyncThunk(
  "aiChats/sendHelpQuery",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.post(
        `${config.API_URL}/ai/help/`,
        params,
        { timeout: 180000 }
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error:
            error.code === "ECONNABORTED"
              ? "The request took too long. Please try again."
              : error.message,
        });
      }
    }
  }
);

// [CANA-718] "Send to support": a help answer the assistant had no guide for can be
// passed to the Canasale team. The team's answer comes back into the same thread as a
// message with role "support".
export const sendHelpSupportRequest = createAsyncThunk(
  "aiChats/sendHelpSupportRequest",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.post(
        `${config.API_URL}/ai/help/support/`,
        params,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

// [CANA-748] An automatic answer went live while the user's request to the team was
// still waiting. `action` is "solved" (close the request) or "need_person" (keep it
// open, or open it again once it was closed).
export const sendHelpSupportFollowup = createAsyncThunk(
  "aiChats/sendHelpSupportFollowup",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.post(
        `${config.API_URL}/ai/help/support/followup/`,
        params,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

// [CANA-804] "Was this helpful?" under a help answer. `rating` is "yes", "partial" or
// "no"; sending another value for the same answer replaces the earlier one.
export const rateHelpAnswer = createAsyncThunk(
  "aiChats/rateHelpAnswer",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.post(
        `${config.API_URL}/ai/help/rate/`,
        params,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

// Which help threads hold a support answer this user has not opened yet. It feeds the
// dot on "AI Help" in the app sidebar and on the thread in the conversation list.
// Automatic answers delivered after a deploy count too (CANA-752).
export const getHelpSupportUnread = createAsyncThunk(
  "aiChats/getHelpSupportUnread",
  async (_, { rejectWithValue }) => {
    try {
      const data = await axiosClient.get(
        `${config.API_URL}/ai/help/support/unread/`,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const markHelpSupportRead = createAsyncThunk(
  "aiChats/markHelpSupportRead",
  async (params, { rejectWithValue }) => {
    try {
      await axiosClient.post(
        `${config.API_URL}/ai/help/support/read/`,
        params,
        config
      );
      return { chat_id: params.chat_id };
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);
